import React, { useMemo } from "react";

/**
 * FilterBar
 * - title + subtitle on the left
 * - controls on the right (search/select/date/custom)
 * - actions on the far right
 *
 * controls: Array of control configs:
 *  - { type: "search", value, onChange, placeholder, width }
 *  - { type: "select", value, onChange, options:[{value,label}], placeholder, width }
 *  - { type: "date", value, onChange, iconClass, width }
 *  - { type: "dateRange", fromValue, toValue, onChangeFrom, onChangeTo }
 *  - { type: "custom", render: () => ReactNode }
 *
 * actions: Array of action configs:
 *  - { label, iconClass, onClick, variant:"primary"|"secondary", disabled }
 */

const baseInputStyle = {
  height: 38,
  borderRadius: 8,
  border: "1px solid #E2E8F0",
  fontSize: 13,
  color: "#4A5568",
  backgroundColor: "#FFFFFF",
  boxShadow: "none",
  textTransform: "none",
};

const secondaryBtnStyle = {
  ...baseInputStyle,
  padding: "0 14px",
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
};

const primaryBtnStyle = {
  height: 38,
  borderRadius: 8,
  border: "none",
  fontWeight: 600,
  padding: "0 18px",
  fontSize: 13,
  display: "flex",
  alignItems: "center",
  gap: 8,
  textTransform: "none",
};

const normalizeOptions = (options = []) =>
  (Array.isArray(options) ? options : []).map((o) => ({
    value: o.value,
    label: o.label ?? o.title ?? String(o.value ?? ""),
  }));


const FilterBar = ({
  title,
  subtitle = "",
  iconClass = "fa fa-filter",
  controls = [],
  actions = [],
  containerClassName = "",
  maxWidth = "100%",
}) => {
  const normalizedControls = useMemo(() => controls || [], [controls]);
  const normalizedActions = useMemo(() => actions || [], [actions]);

  return (
    <div className={`container-fluid py-3 border-bottom bg-white ${containerClassName}`}>
      <div className="row align-items-center">
        {/* Left: Title */}
        <div className="col-12 col-lg-3 mb-2 mb-lg-0">
          <div className="d-flex align-items-center" style={{ gap: 10 }}>
            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: "rgba(79,70,229,.08)",
                flex: "0 0 auto",
              }}
            >
              <i className={`${iconClass} text-primary`} />
            </div>

            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: "#1A202C", textTransform: "none" }}>
                {title}
              </div>
              {subtitle ? (
                <div className="text-muted" style={{ fontSize: 12, textTransform: "none" }}>
                  {subtitle}
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Right: Controls + actions */}
        <div className="col-12 col-lg-9">
          <div
            className="d-flex justify-content-lg-end align-items-center flex-wrap"
            style={{ gap: 10, maxWidth }}
          >
            {/* Controls */}
            {normalizedControls.map((c, idx) => {
              if (c.type === "custom" && typeof c.render === "function") {
                return <div key={idx}>{c.render()}</div>;
              }

              if (c.type === "search") {
                const w = c.width ?? 260;
                return (
                  <div key={idx} className="input-group custom-search-group mr-2" 
                    style={{ 
                        width: "240px", 
                        borderRadius: "8px", 
                        border: "1px solid #E2E8F0", // Move border to parent
                        transition: "all 0.2s" 
                    }}>
                    <div className="input-group-prepend">
                      <span
                        className="input-group-text bg-light border-0"
                        style={{
                          borderRadius: "8px 0 0 8px",
                        }}
                      >
                        <i className={`fa fa-search text-muted`} style={{ fontSize: 12 }} />
                      </span>
                    </div>
                    <input
                      type="text"
                      className="form-control border-0 bg-transparent shadow-none"
                      placeholder={c.placeholder ?? "Search..."}
                      value={c.value ?? ""}
                      onChange={c.onChange}
                      onKeyDown={(e) => e.key === "Enter" && c.onEnter?.(e)}
                      style={{ 
                        height: "38px", 
                        fontSize: "13px", 
                        color: "#4A5568" 
                    }}
                    />
                  </div>
                );
              }

              if (c.type === "select") {
                const w = c.width ?? 170;
                const opts = normalizeOptions(c.options);

                return (
                    <select
                    key={idx}
                    className="form-control custom-select shadow-none"
                    value={String(c.value ?? "")}                 // ✅ force string
                    onChange={(e) => c.onChange?.(e)}             // keep your handler
                    style={{ ...baseInputStyle, width: w }}
                    >
                    {c.placeholder !== undefined && <option value="">{c.placeholder}</option>}

                    {opts.map((o, i) => (
                        <option value={String(o.value)} key={i}>    {/* ✅ force string */}
                        {o.label}
                        </option>
                    ))}
                    </select>
                );
                }


              if (c.type === "date") {
                const w = c.width ?? 190;
                return (
                  <div key={idx} className="input-group" style={{ width: w }}>
                    <div className="input-group-prepend">
                      <span
                        className="input-group-text bg-light border-right-0"
                        style={{
                          borderRadius: "8px 0 0 8px",
                          border: "1px solid #E2E8F0",
                          height: 38,
                        }}
                      >
                        <i className={`${c.iconClass ?? "fa fa-calendar-alt"} text-muted`} style={{ fontSize: 12 }} />
                      </span>
                    </div>
                    <input
                      type="date"
                      className="form-control shadow-none border-left-0"
                      value={c.value ?? ""}
                      onChange={c.onChange}
                      style={{ ...baseInputStyle, borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                    />
                  </div>
                );
              }

              if (c.type === "dateRange") {
                return (
                  <div
                    key={idx}
                    className="d-flex align-items-center px-2"
                    style={{
                      borderRadius: 8,
                      border: "1px solid #E2E8F0",
                      backgroundColor: "#F8FAFC",
                      height: 38,
                    }}
                  >
                    <input
                      type="date"
                      className="form-control border-0 bg-transparent shadow-none"
                      value={c.fromValue ?? ""}
                      onChange={c.onChangeFrom}
                      style={{ height: 36, fontSize: 12, width: 130, padding: "0 6px" }}
                    />
                    <span className="text-muted small mx-1">to</span>
                    <input
                      type="date"
                      className="form-control border-0 bg-transparent shadow-none"
                      value={c.toValue ?? ""}
                      onChange={c.onChangeTo}
                      style={{ height: 36, fontSize: 12, width: 130, padding: "0 6px" }}
                    />
                  </div>
                );
              }

              return null;
            })}

            {/* Actions */}
            {normalizedActions.length > 0 && (
              <div className="d-flex" style={{ gap: 8 }}>
                {normalizedActions.map((a, idx) => {
                  const isPrimary = a.variant === "primary";
                  if (!isPrimary) {
                    return (
                      <button
                        key={idx}
                        type="button"
                        className="btn bg-white shadow-sm border"
                        style={secondaryBtnStyle}
                        onClick={a.onClick}
                        disabled={a.disabled}
                      >
                        {a.iconClass ? <i className={`${a.iconClass} text-muted`} /> : null}
                        <span style={{ color: "#4A5568" }}>{a.label}</span>
                      </button>
                    );
                  }

                  return (
                    <button
                      key={idx}
                      type="button"
                      className="btn btn-primary d-flex align-items-center shadow-sm"
                      onClick={a.onClick}
                      disabled={a.disabled}
                      style={{
                        ...primaryBtnStyle,
                        backgroundColor: a.color ?? "#4F46E5",
                      }}
                    >
                      {a.iconClass ? <i className={a.iconClass} style={{ fontSize: 11 }} /> : null}
                      <span>{a.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default FilterBar;
